import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {BaseDialogComponent} from '@q9elements/ui-kit/common';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {filter} from 'lodash';
import {BehaviorSubject, Observable} from 'rxjs';

import {CustomViewService} from '../../../../services/custom-view.service';
import {ISelectedMetadata} from '../../dialogs/models/create-new-view.interface';

const GLOBAL_VIEW = 'global';
const PRIVATE_VIEW = 'private';

@Component({
  selector: 'app-select-metadata-view',
  templateUrl: './select-metadata-view.component.html',
  styleUrls: ['./select-metadata-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectMetadataViewComponent extends BaseDialogComponent implements OnInit {
  @Input() public disableGlobalViewCreation: boolean;
  @Input() public disablePrivateViewCreation: boolean;
  @Input() public isViewer = false;
  @Output() public confirmSelected: EventEmitter<ISelectedMetadata> =
    new EventEmitter<ISelectedMetadata>();
  /** Live draft of this step, emitted on every change so the host can track validity/dirtiness. */
  @Output() public scopeStateChange: EventEmitter<{scope: ISelectedMetadata; valid: boolean}> =
    new EventEmitter<{scope: ISelectedMetadata; valid: boolean}>();

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public selectMetadataForm: UntypedFormGroup;

  public metadataTypes: string[];
  private allNodes = false;

  public selectedMetadata: string[] = [];
  public availableMetadata: string[] = [];

  public isViewValid = false;
  public isPrivateView: boolean;
  public disableTooltip: string;
  /** Viewer at the private-view limit: nothing can be created from this dialog. */
  public blockCreation: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private customViewService: CustomViewService,
    public dialogRef: MatDialogRef<SelectMetadataViewComponent>,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isPrivateView = this.disableGlobalViewCreation && !this.disablePrivateViewCreation;
    this.blockCreation = this.isViewer && this.disablePrivateViewCreation;

    this.setTooltip();
    this.initMetadataView();
    this.initSelectMetadataForm();
    this.initFormValidate();
    this.validate();

    this.loading$.next(false);
  }

  private initMetadataView() {
    const preSelectedMetadata = this.customViewService.retrieveSelectedMetadata();
    const metadata = this.customViewService.retrieveRefModelMetadata();

    this.metadataTypes = [...metadata.list];

    this.allNodes = preSelectedMetadata.allNodes;
    this.selectedMetadata = !this.allNodes
      ? preSelectedMetadata.selectedItems?.slice() || []
      : this.metadataTypes.slice();

    this.initMetadataLists();
    this.validate();
  }

  private initMetadataLists() {
    this.availableMetadata = !this.allNodes
      ? filter(this.metadataTypes, type => !this.selectedMetadata.includes(type))
      : [];
  }

  private initSelectMetadataForm() {
    const gridName = this.customViewService.retrieveGridName();
    const gridDescription = this.customViewService.retrieveGridDescription();
    // Disabled private creation forces global; otherwise forced-private wins, then
    // the last committed choice (so it survives step navigation).
    const privateView =
      !this.disablePrivateViewCreation &&
      (this.isPrivateView || this.customViewService.retrieveIsPrivateGrid());

    const form = {
      gridName: [
        gridName || '',
        Q9Validators.compose([
          Q9Validators.required(),
          Q9Validators.minLength(2),
          Q9Validators.maxLength(40)
        ])
      ],
      gridDescription: [
        gridDescription || '',
        Q9Validators.compose([Q9Validators.minLength(2), Q9Validators.maxLength(500)])
      ],
      isPrivate: [
        {
          value: privateView,
          disabled: this.disableGlobalViewCreation || this.disablePrivateViewCreation
        }
      ]
    };

    this.selectMetadataForm = this.formBuilder.group(form);
  }

  private checkIsAllNodes() {
    this.allNodes = this.selectedMetadata.length === this.metadataTypes.length;
  }

  private initFormValidate(): void {
    this.selectMetadataForm.valueChanges
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(() => this.validate());
  }

  private buildScopePayload(): ISelectedMetadata {
    return {
      ...(!this.allNodes && {selectedItems: this.selectedMetadata}),
      allNodes: this.allNodes
    } as ISelectedMetadata;
  }

  /**
   * Mirror the whole step draft (name, description, privacy, metadata scope) into
   * the shared store on every change, so leaving the step via the stepper header
   * never loses edits. Validity gates navigation, not persistence.
   */
  private persistScopeState(): void {
    if (!this.selectMetadataForm) {
      return;
    }

    const {gridName, gridDescription, isPrivate} = this.selectMetadataForm.controls;
    const scope = this.buildScopePayload();

    this.customViewService.storeGridName(gridName.value);
    this.customViewService.storeGridDescription(gridDescription.value);
    this.customViewService.storeIsPrivateGrid(isPrivate.value);
    this.customViewService.storeSelectedMetadata(scope);

    this.scopeStateChange.emit({scope, valid: this.isViewValid});
  }

  private setTooltip() {
    if (this.blockCreation) {
      this.disableTooltip = this.translateService.instant(
        'RUBIC_CUBE.PRIVATE_VIEW_LIMIT_REACHED_TOOLTIP'
      );
    } else if (this.isViewer) {
      this.disableTooltip = this.translateService.instant(
        'RUBIC_CUBE.VIEWERS_PRIVATE_ONLY_TOOLTIP'
      );
    } else if (this.disableGlobalViewCreation) {
      this.disableTooltip = this.translateService.instant('RUBIC_CUBE.CANT_CREATE_TYPE_VIEW', {
        type: GLOBAL_VIEW
      });
    } else if (this.disablePrivateViewCreation) {
      this.disableTooltip = this.translateService.instant('RUBIC_CUBE.CANT_CREATE_TYPE_VIEW', {
        type: PRIVATE_VIEW
      });
    }
  }

  public validate(): void {
    this.checkIsAllNodes();

    this.isViewValid =
      this.selectMetadataForm?.controls.gridName.valid && this.selectedMetadata.length >= 1;

    this.persistScopeState();
  }

  override onSubmit() {
    if (this.selectMetadataForm.invalid) {
      this.selectMetadataForm.markAllAsTouched();
      this.selectMetadataForm.markAsDirty();
      return;
    }

    this.sendSubmitRequest();
  }

  protected override sendSubmitRequest(): Observable<unknown> {
    this.persistScopeState();
    this.confirmSelected.emit(this.buildScopePayload());

    return;
  }
}
